import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Brush, CircleSlash, FileText, Pencil, Repeat, Send } from 'lucide-react'
import {
  Alert, Card, Field, Input, Loading, Modal, StatusPill, Textarea, useToast,
} from '@/components/ui'
import { AttachmentList, AuditTrail } from '@/components/docs/shared'
import { DocumentActions } from '@/components/docs/DocumentActions'
import { ApprovalTrail } from '@/components/docs/ApprovalTrail'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { DeleteDocument } from '@/components/docs/DeleteDocument'
import { fetchLines, fetchOne, useMaterials, usePeople, useSiteNames } from '@/lib/data'
import { can, isEditable, round2 } from '@/lib/domain'
import { ddmmyyyy, dateTime, qty, rupees } from '@/lib/format'
import { enqueue } from '@/lib/sync'
import { localDb } from '@/lib/db'
import type { Material, PrLine, PurchaseRequisition } from '@/lib/types'

export function PrDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  // Names, not email addresses — management 01-09-2026. See src/lib/people.ts.
  const people = usePeople()
  const { siteName } = useSiteNames()
  const toast = useToast()
  const navigate = useNavigate()

  const [pr, setPr] = useState<PurchaseRequisition | null>(null)
  const [lines, setLines] = useState<PrLine[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [istOpen, setIstOpen] = useState(false)
  // Stage 2 of the Board's chain belongs to the Project Manager assigned to
  // THIS site, so the screen has to know who that is before it offers buttons.
  const [sitePmId, setSitePmId] = useState<string | null>(null)
  // Transfers already raised against THIS requisition, when it is an IST one.
  // What decides whether the page shouts at him or merely reports.
  const [istTransfers, setIstTransfers] = useState<any[]>([])
  const [istCovered, setIstCovered] = useState(true)
  const [cancelIstOpen, setCancelIstOpen] = useState(false)

  const { data: materials } = useMaterials()
  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>,
    [materials])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const header = await fetchOne<PurchaseRequisition>('purchase_requisitions', id)
      if (!header) throw new Error(`Requisition ${id} was not found, or you do not have access to it.`)
      setPr(header)
      setLines(await fetchLines<PrLine>('pr_line_items', 'pr_id', id))
      const { data: s } = await supabase
        .from('sites').select('project_manager_id').eq('id', header.site_id).maybeSingle()
      setSitePmId((s as any)?.project_manager_id ?? null)

      // Is every line of this IST requisition on a transfer yet? Read from
      // v_ist_pr_allocation, which already nets off cancelled transfers and is
      // the same figure the database uses to decide the dashboard reminder —
      // so the page and the dashboard can never disagree.
      if (header.pr_type === 'IST') {
        const [t, a] = await Promise.all([
          supabase.from('inter_site_transfers').select('id, status, from_site_id')
            .eq('ist_pr_id', id).order('created_at'),
          supabase.from('v_ist_pr_allocation').select('qty_open').eq('pr_id', id),
        ])
        setIstTransfers(t.data ?? [])
        setIstCovered(((a.data ?? []) as any[]).every((r) => Number(r.qty_open) <= 0.001))
      } else {
        setIstTransfers([]); setIstCovered(true)
      }
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the requisition')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  if (loading) return <Loading label="Loading requisition…" />
  if (error || !pr) return <Alert tone="error">{error}</Alert>

  const role = profile!.role
  const isOriginator = pr.creator_email === profile!.email
  const canSubmit = isEditable(pr.status) && isOriginator
  // A returned requisition goes back to whoever raised it, contents and all.
  const canCorrect = isEditable(pr.status) && (isOriginator || role === 'admin')
  // Carving an internal transfer out of a requisition is a decision the Head of
  // Stores takes *while* the requisition is on his desk. Once he has approved
  // it, the requisition belongs to Procurement and its quantities are what the
  // purchase order is being built from — moving lines out from under it at that
  // point would silently change what has already been authorised.
  const canCarveIst = pr.pr_type === 'STANDARD' && pr.status === 'SUBMITTED' && can(role, 'createIstPr')
  const canRaisePo = ['APPROVED', 'PARTIALLY_FULFILLED'].includes(pr.status) && pr.pr_type === 'STANDARD' && can(role, 'createPo')
  // 0188: what Procurement declared no longer required is not open either.
  const openOf = (l: PrLine) => round2(l.quantity_requested - l.quantity_on_po - l.quantity_on_ist - (l.quantity_closed ?? 0))
  const openLines = lines.filter((l) => openOf(l) > 0.001)
  const anyClosed = lines.some((l) => (l.quantity_closed ?? 0) > 0.0005)
  // 0211: a transfer requisition whose open quantity is no longer required.
  // Nothing delivered -> cancelled; part delivered -> the rest closed. Not
  // while a transfer is still in draft or on the way - rpc_ist_pr_cancel
  // checks all of this again.
  const isOpenIst = pr.pr_type === 'IST' && ['APPROVED', 'PARTIALLY_FULFILLED'].includes(pr.status)
  const istInFlight = istTransfers.some((t: any) => ['DRAFT', 'TRANSFER_INITIATED', 'IN_TRANSIT'].includes(t.status))
  const istDelivered = lines.some((l) => l.quantity_on_ist > 0.0005)
  const canCancelIst = isOpenIst && can(role, 'cancelIstPr') && !istInFlight && openLines.length > 0

  async function submitPr() {
    await enqueue({
      kind: 'pr.submit', docType: 'PR', localDocId: pr!.id,
      label: `Submit requisition ${pr!.id}`, payload: { id: pr!.id },
    })
    toast.success('Submitted to the Head of Stores.')
    void load()
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/prs" className="btn-ghost btn-sm"><ArrowLeft className="h-4 w-4" /> All requisitions</Link>
          <h1 className="font-mono text-base font-bold text-slate-800">{pr.id}</h1>
          <StatusPill status={pr.status} />
          {pr.pr_type === 'IST' && <span className="pill-violet">Inter-site transfer requisition</span>}
          {pr.is_legacy && <span className="pill-grey">migrated</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          {canCorrect && (
            <Link to={`/prs/${pr.id}/edit`} className="btn-primary btn-sm">
              <Pencil className="h-4 w-4" /> {pr.status === 'RETURNED' ? 'Correct and resubmit' : 'Edit'}
            </Link>
          )}
          {canSubmit && <button className="btn-secondary btn-sm" onClick={submitPr}><Send className="h-4 w-4" /> Submit</button>}
          <DeleteDocument docType="PR" docId={pr.id} status={pr.status}
                          creatorEmail={pr.creator_email} backTo="/prs" />
          {canCarveIst && (
            <button className="btn-secondary btn-sm" onClick={() => setIstOpen(true)}>
              <Repeat className="h-4 w-4" /> Fulfil part internally (IST)
            </button>
          )}
          {canCancelIst && (
            <button className="btn-danger btn-sm" onClick={() => setCancelIstOpen(true)}
                    title="The material is no longer required - no transfer will be raised">
              <CircleSlash className="h-4 w-4" />
              {istDelivered ? 'Close the rest — not required' : 'Cancel this transfer requisition'}
            </button>
          )}
          {canRaisePo && can(role, 'prHousekeeping') && (
            <Link to={`/pr-housekeeping?pr=${pr.id}`} className="btn-secondary btn-sm"
                  title="Cancel it, or close what is no longer required">
              <Brush className="h-4 w-4" /> Housekeeping
            </Link>
          )}
          {canRaisePo && (
            <Link to={`/pos/new?pr=${pr.id}`} className="btn-primary btn-sm">
              <FileText className="h-4 w-4" /> Create purchase order
            </Link>
          )}
        </div>
      </div>

      {/* Requisition housekeeping, 0188. */}
      {pr.status === 'CANCELLED' && pr.cancel_reason && pr.pr_type === 'IST' && (
        <Alert tone="warning" title="Transfer requisition cancelled — nothing will be transferred">
          {pr.cancel_reason}
          {pr.cancelled_at && <> · {dateTime(pr.cancelled_at)}</>}
          {pr.parent_pr_id && (
            <> {' '}The quantity carved out of{' '}
              <Link className="underline" to={`/prs/${pr.parent_pr_id}`}>{pr.parent_pr_id}</Link>
              {' '}was not required and does not return to it.</>
          )}
        </Alert>
      )}
      {pr.status === 'CANCELLED' && pr.cancel_reason && pr.pr_type !== 'IST' && (
        <Alert tone="warning" title="Cancelled by Procurement — nothing will be ordered">
          {pr.cancel_reason}
          {pr.cancelled_at && <> · {dateTime(pr.cancelled_at)}</>}
        </Alert>
      )}
      {pr.status === 'CLOSED' && (
        <Alert tone="warning" title="Closed — the rest is no longer required">
          {pr.short_close_reason ?? 'Closed when the last open quantity was declared no longer required.'}
          {pr.short_closed_at && <> · {dateTime(pr.short_closed_at)}</>}
          {' '}What was ordered or transferred stays as it is; the column “Not required” shows the rest.
        </Alert>
      )}

      {pr.status === 'REJECTED' && (
        <Alert tone="error" title="This requisition was rejected and is permanently stopped">
          {pr.rejection_reason ?? 'No reason was recorded.'}
          {' '}A fresh requisition has to be raised — this one cannot be revived.
        </Alert>
      )}
      {pr.status === 'RETURNED' && (
        <Alert tone="warning" title="Returned for revision">
          {pr.return_reason ?? 'No note was recorded.'}
          {isOriginator && ' Correct the requisition and submit it again.'}
        </Alert>
      )}
      {/* -------------------------------------------------------------------
          An IST requisition that reads APPROVED is not finished. Until a
          transfer is raised against it the material has not moved and the site
          that asked for it is still waiting — and before 09-09-2026 nothing on
          this page said so above a whisper. It is a call to act, not a note. */}
      {pr.pr_type === 'IST' && !['CANCELLED', 'CLOSED'].includes(pr.status) && (
        istCovered ? (
          <Alert tone="info" title="Inter-site transfer requisition">
            Carved out of{' '}
            {pr.parent_pr_id ? <Link className="underline" to={`/prs/${pr.parent_pr_id}`}>{pr.parent_pr_id}</Link> : 'a parent requisition'}
            {' '}and auto-approved. Every line is on a transfer:{' '}
            {istTransfers.map((t: any, i: number) => (
              <span key={t.id}>
                {i > 0 && ', '}
                <Link className="font-mono underline" to={`/ists/${t.id}`}>{t.id}</Link>
                {' '}({String(t.status).replace(/_/g, ' ').toLowerCase()})
              </span>
            ))}.
          </Alert>
        ) : (
          <div className="rounded-lg border-2 border-amber-400 border-l-4 border-l-amber-600 bg-amber-50 p-4">
            <div className="flex flex-wrap items-start gap-3">
              <Repeat className="mt-0.5 h-6 w-6 shrink-0 text-amber-700" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-amber-900">
                  This requisition is approved, but nothing has moved yet
                </p>
                <p className="mt-1 text-sm text-amber-900">
                  It was carved out of{' '}
                  {pr.parent_pr_id
                    ? <Link className="font-semibold underline" to={`/prs/${pr.parent_pr_id}`}>{pr.parent_pr_id}</Link>
                    : 'a parent requisition'}
                  {' '}and auto-approved because you raised it yourself. The material still has to be
                  transferred — <strong>raise the transfer now</strong>, or this will sit on your dashboard
                  until you do.
                  {istTransfers.length > 0 && (
                    <> Some of it is already on{' '}
                      {istTransfers.map((t: any, i: number) => (
                        <span key={t.id}>
                          {i > 0 && ', '}
                          <Link className="font-mono underline" to={`/ists/${t.id}`}>{t.id}</Link>
                        </span>
                      ))}.
                    </>
                  )}
                </p>
              </div>
              <Link to={`/ists/new?pr=${pr.id}`} className="btn-primary shrink-0">
                <Repeat className="h-4 w-4" /> Raise the transfer
              </Link>
            </div>
          </div>
        )
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Requisition details" className="lg:col-span-2">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Site" value={siteName(pr.site_id)} />
            <Detail label="Raised by" value={people.name({ email: pr.creator_email })} />
            <Detail label="Raised on" value={ddmmyyyy(pr.date_created)} />
            <Detail label="Urgency" value={pr.urgency} />
            <Detail label="Needed by" value={ddmmyyyy(pr.required_by_date)} />
            <Detail label="Estimated value" value={rupees(pr.total_estimated_amount)} />
            <Detail label="Approved by" value={pr.approver_email ? people.name({ email: pr.approver_email }) : '—'} />
            <Detail label="Approved on" value={pr.approval_date ? dateTime(pr.approval_date) : '—'} />
            <Detail label="Open lines" value={`${openLines.length} of ${lines.length}`} />
          </dl>
          {pr.remarks && (
            <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <p className="text-xs font-semibold text-slate-500">Remarks</p>
              <p className="text-slate-700">{pr.remarks}</p>
            </div>
          )}
        </Card>

        <Card title="Decision" subtitle="Approve, return, or reject">
          <DocumentActions docType="PR" docId={pr.id} status={pr.status}
                           chainVersion={(pr as any).chain_version}
                           currentStep={(pr as any).current_step}
                           sitePmId={sitePmId}
                           creatorId={(pr as any).requested_by}
                           onDone={load} className="flex flex-col gap-2" />
          {!['SUBMITTED'].includes(pr.status) && (
            <p className="text-sm text-slate-500">
              {pr.status === 'DRAFT' ? 'Still a draft — the originator has to submit it first.'
                : 'No decision is pending on this requisition.'}
            </p>
          )}
          <div className="mt-3">
            <ApprovalTrail docType="PR" docId={pr.id} status={pr.status}
                           chainVersion={(pr as any).chain_version}
                           currentStep={(pr as any).current_step} />
          </div>
        </Card>
      </div>

      <Card title="Materials requested" subtitle="Quantities already covered by a PO or an internal transfer are shown separately">
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th><th>Material</th><th>Code</th>
                <th className="num">Requested</th><th className="num">On a PO</th>
                <th className="num">Via IST</th>
                {anyClosed && <th className="num">Not required</th>}
                <th className="num">Still open</th>
                <th className="num">Est. rate</th><th className="num">Est. amount</th>
                <th>Specification</th><th>Needed by</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const m = byId[l.material_id]
                const open = openOf(l)
                return (
                  <tr key={l.id}>
                    <td className="text-xs text-slate-400">{l.sl_no}</td>
                    <td className="text-sm font-medium">{m?.item_name ?? l.material_id}</td>
                    <td className="font-mono text-xs text-slate-500">{m?.item_code ?? '—'}</td>
                    <td className="num">{qty(l.quantity_requested)} <span className="text-xs text-slate-400">{l.uom}</span></td>
                    <td className="num text-xs text-slate-500">{qty(l.quantity_on_po)}</td>
                    <td className="num text-xs text-slate-500">{qty(l.quantity_on_ist)}</td>
                    {anyClosed && <td className="num text-xs text-slate-500">{(l.quantity_closed ?? 0) > 0.0005 ? qty(l.quantity_closed ?? 0) : '—'}</td>}
                    <td className={`num font-semibold ${open > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{qty(open)}</td>
                    <td className="num text-xs">{l.estimated_rate ? rupees(l.estimated_rate) : '—'}</td>
                    <td className="num text-xs">{rupees(l.line_amount)}</td>
                    <td className="max-w-[200px] truncate text-xs text-slate-500">{l.specifications ?? '—'}</td>
                    <td className="text-xs">{ddmmyyyy(l.required_by_date)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <AttachmentList docType="PR" docId={pr.id}
                      subtitle="Anything the site attached to support this requisition" />

      <AuditTrail docType="PR" docId={pr.id} />

      <IstCarveOutModal
        open={istOpen}
        onClose={() => setIstOpen(false)}
        pr={pr}
        lines={openLines}
        materials={byId}
        onDone={() => { setIstOpen(false); void load() }}
      />

      {cancelIstOpen && (
        <CancelIstPrModal pr={pr} delivered={istDelivered} onClose={() => setCancelIstOpen(false)}
                          onDone={() => { setCancelIstOpen(false); void load() }} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// 0211, 14-10-2026. Head of Stores: what is still open on this transfer
// requisition is no longer required. Nothing delivered -> cancelled; part
// delivered -> the rest closed, the delivered part untouched. Nothing returns
// to the parent requisition; it leaves the dashboard. Online only, like
// Requisition Housekeeping - the database decides, and its refusal is shown.
// ---------------------------------------------------------------------------
function CancelIstPrModal({ pr, delivered, onClose, onDone }: {
  pr: PurchaseRequisition; delivered: boolean; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const reasonOk = reason.trim().length >= 10

  async function doCancel() {
    setBusy(true)
    const { data, error } = await supabase.rpc('rpc_ist_pr_cancel', { p_pr_id: pr.id, p_reason: reason.trim() })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(data === 'CLOSED'
      ? `${pr.id} closed — the rest is not required. The site has been told.`
      : `${pr.id} cancelled. The site has been told.`)
    onDone()
  }

  return (
    <Modal open onClose={onClose} title={delivered ? `Close the rest of ${pr.id}?` : `Cancel ${pr.id}?`}
           subtitle="What is still open is no longer required"
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Keep it</button>
             <button className="btn-danger" onClick={() => void doCancel()} disabled={busy || !reasonOk}>
               {busy ? 'Saving…' : delivered ? 'Yes, close the rest' : 'Yes, cancel it'}
             </button>
           </>}>
      <div className="space-y-3">
        <p className="text-sm">
          {delivered
            ? <>What was already delivered stays exactly as it is. Only the quantity still open is marked
                not required; the requisition becomes Closed and leaves your dashboard.</>
            : <>No transfer will be raised and it leaves your dashboard.</>}
          {pr.parent_pr_id && <> The quantity taken off <span className="font-mono">{pr.parent_pr_id}</span> when
          this was carved out does <strong>not</strong> go back to it, so nothing will be bought instead.</>}
          {' '}The site's Project Manager is told why. <strong>This cannot be undone.</strong>
        </p>
        <Field label="Why?" required hint="At least a short sentence. It is kept permanently in the audit trail.">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Work at site changed; the material is no longer required." />
        </Field>
      </div>
    </Modal>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-slate-800">{value}</dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Head of Stores: carve the internally-fulfillable items out of this PR into a
// special, auto-approved IST requisition. The moved quantity is removed from
// this PR so procurement never buys it.
// ---------------------------------------------------------------------------
function IstCarveOutModal({ open, onClose, pr, lines, materials, onDone }: {
  open: boolean; onClose: () => void
  pr: PurchaseRequisition; lines: PrLine[]
  materials: Record<string, Material>
  onDone: () => void
}) {
  const toast = useToast()
  const navigate = useNavigate()
  const [picked, setPicked] = useState<Record<string, string>>({})
  const [remarks, setRemarks] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (open) { setPicked({}); setRemarks('') } }, [open])

  const chosen = Object.entries(picked).filter(([, v]) => Number(v) > 0)

  async function create() {
    if (chosen.length === 0) return
    setBusy(true)
    try {
      const entry = await enqueue({
        kind: 'ist_pr.create', docType: 'PR', localDocId: pr.id,
        label: `Create IST requisition from ${pr.id}`,
        payload: {
          parent_pr_id: pr.id,
          items: chosen.map(([pr_line_id, quantity]) => ({ pr_line_id, quantity: Number(quantity) })),
          remarks: remarks || null,
        },
      })

      // The new requisition's number is minted by the database, so it is not
      // known here until the outbox has actually sent this entry. enqueue()
      // flushes before it returns when we are online, but the object it hands
      // back was captured BEFORE that — so the id is read back off the queue.
      const sent = entry.id ? await localDb.outbox.get(entry.id) : undefined
      const newPrId = sent?.server_doc_id ?? null

      if (newPrId) {
        // Reported 09-09-2026: the requisition was created, read APPROVED,
        // and nothing told him a transfer still had to be raised. He is taken
        // to it now, and it stays on his dashboard until one exists.
        toast.success(`${newPrId} created and auto-approved. Now raise the transfer that moves the material.`)
        onClose()
        navigate(`/prs/${newPrId}`)
        return
      }

      // Offline, or the queue has not drained. Say so plainly rather than
      // navigating to a document that does not exist yet.
      toast.success('Queued. The IST requisition will be created when this device is back online, '
                  + 'and will then appear on your dashboard until you raise the transfer.')
      onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not create the IST requisition')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Fulfil part of this requisition internally"
      subtitle="Pick the items you can move from another site's store"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={create} disabled={busy || chosen.length === 0}>
            Create IST requisition ({chosen.length})
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert tone="info">
          A new requisition of type <strong>IST</strong> is created and auto-approved, because you are raising it
          yourself. The quantities you enter here are <strong>deducted from {pr.id}</strong>, so the Procurement
          Manager only ever sees what genuinely has to be bought.
        </Alert>

        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Material</th><th className="num">Open on this PR</th><th className="num">Move to IST</th></tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const m = materials[l.material_id]
                const open = round2(l.quantity_requested - l.quantity_on_po - l.quantity_on_ist - (l.quantity_closed ?? 0))
                return (
                  <tr key={l.id}>
                    <td>
                      <span className="block text-sm font-medium">{m?.item_name ?? l.material_id}</span>
                      <span className="block font-mono text-xs text-slate-500">{m?.item_code}</span>
                    </td>
                    <td className="num">{qty(open)} {l.uom}</td>
                    <td className="num">
                      <input
                        type="number" min="0" max={open} step="any" inputMode="decimal"
                        className="field w-28 text-right"
                        placeholder="0"
                        value={picked[l.id] ?? ''}
                        onChange={(e) => {
                          const v = Math.min(Number(e.target.value) || 0, open)
                          setPicked((p) => ({ ...p, [l.id]: v ? String(v) : '' }))
                        }}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Field label="Remarks" hint="Which site the stock is coming from, and why">
          <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                    placeholder="e.g. Surplus 12mm TMT at Khubal ONGC GCS — moving 2 MT" />
        </Field>
      </div>
    </Modal>
  )
}
